import { getTranscriptionSettings, updateTranscriptionSettings, setTranscriptionPreset } from '../../../goServerAPI/transcription.js';

export interface VADSettings {
  frameMs: number;
  preRollMs: number;
  minSpeechMs: number;
  hangoverMs: number;
  finalSilenceMs: number;
  maxUtterMs: number;
  sliceMs: number;
  windowSec: number;
  stepMillis: number;
  firstWindowMillis: number;
}

export interface TranscriptionSettings {
  language: string;
  sampleRate: number;
  maxInFlight: number;
  chunkTimeout: number;
  preset: string;
  vadSettings: VADSettings;
}

export interface TranscriptionSettingsState {
  settings: TranscriptionSettings | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  success: string | null;
}

export interface TranscriptionSettingsActions {
  loadSettings: () => Promise<void>;
  updateSettings: (settings: TranscriptionSettings) => Promise<void>;
  setPreset: (preset: string) => Promise<void>;
  saveToLocalStorage: () => void;
  loadFromLocalStorage: () => void;
  clearMessages: () => void;
  clearLocalStorage: () => void;
}

const STORAGE_KEY = 'transcription-settings';

const defaultSettings: TranscriptionSettings = {
  language: 'he',
  sampleRate: 16000,
  maxInFlight: 4,
  chunkTimeout: 30,
  preset: 'fast_response',
  vadSettings: {
    frameMs: 10,
    preRollMs: 100,
    minSpeechMs: 200,
    hangoverMs: 200,
    finalSilenceMs: 1000,
    maxUtterMs: 10000,
    sliceMs: 1000,
    windowSec: 2,
    stepMillis: 1000,
    firstWindowMillis: 1000,
  },
};

export const createTranscriptionSettingsModule = (
  set: (partial: any) => void,
  get: () => any,
  getSDK: () => any
): TranscriptionSettingsState & TranscriptionSettingsActions => ({
  // Initial state
  settings: null,
  loading: false,
  saving: false,
  error: null,
  success: null,

  // Actions
  loadSettings: async () => {
    try {
      set({ loading: true, error: null });
      
      // Get base URL from SDK
      const sdk = getSDK();
      if (!sdk) {
        throw new Error('SDK not initialized');
      }
      
      // Try to load from server first
      const serverSettings = await getTranscriptionSettings(sdk._cfg.baseURL);
      set({ settings: serverSettings, loading: false });
      
      // Save to localStorage for offline access
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serverSettings));
    } catch (error) {
      console.error('Failed to load settings from server, trying localStorage:', error);
      
      // Fallback to localStorage
      const localSettings = localStorage.getItem(STORAGE_KEY);
      if (localSettings) {
        try {
          const parsed = JSON.parse(localSettings);
          set({ settings: parsed, loading: false });
        } catch (parseError) {
          console.error('Failed to parse localStorage settings:', parseError);
          set({ 
            settings: defaultSettings, 
            loading: false, 
            error: 'Failed to load settings from server and localStorage' 
          });
        }
      } else {
        set({ 
          settings: defaultSettings, 
          loading: false, 
          error: 'No settings found. Using defaults.' 
        });
      }
    }
  },

  updateSettings: async (settings: TranscriptionSettings) => {
    try {
      set({ saving: true, error: null, success: null });
      
      // Get base URL from SDK
      const sdk = getSDK();
      if (!sdk) {
        throw new Error('SDK not initialized');
      }
      
      await updateTranscriptionSettings(settings, sdk._cfg.baseURL);
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      
      set({ 
        settings, 
        saving: false, 
        success: 'Settings saved successfully' 
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      set({ 
        saving: false, 
        error: 'Failed to save settings' 
      });
    }
  },

  setPreset: async (preset: string) => {
    try {
      set({ saving: true, error: null, success: null });
      
      // Get base URL from SDK
      const sdk = getSDK();
      if (!sdk) {
        throw new Error('SDK not initialized');
      }
      
      const result = await setTranscriptionPreset(preset, sdk._cfg.baseURL);
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(result.settings));
      
      set({ 
        settings: result.settings, 
        saving: false, 
        success: `Preset "${preset}" applied successfully` 
      });
    } catch (error) {
      console.error('Error setting preset:', error);
      set({ 
        saving: false, 
        error: 'Failed to apply preset' 
      });
    }
  },

  saveToLocalStorage: () => {
    const { settings } = get();
    if (settings) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    }
  },

  loadFromLocalStorage: () => {
    try {
      const localSettings = localStorage.getItem(STORAGE_KEY);
      if (localSettings) {
        const parsed = JSON.parse(localSettings);
        set({ settings: parsed });
      }
    } catch (error) {
      console.error('Failed to load settings from localStorage:', error);
    }
  },

  clearMessages: () => {
    set({ error: null, success: null });
  },

  clearLocalStorage: () => {
    localStorage.removeItem(STORAGE_KEY);
    console.log('[STORE] Cleared localStorage for transcription settings');
  },
});
